/* ==========================================================================
   PAMPER & PEACE — scent-quiz.js
   Step navigation (slide + fade), affinity scoring, email gate (Klaviyo),
   and inline result reveal.

   Initialises EVERY [data-quiz] on the page (each is scoped to its own root),
   so the inline quiz and the pop-up quiz can coexist — e.g. on the homepage,
   which renders both. Safe to run twice (per-root guard) and re-runs for
   sections re-rendered by the theme editor.
   ========================================================================== */
(function () {
  'use strict';
  const Pamper = (window.Pamper = window.Pamper || {});

  function initQuiz(quiz) {
    if (!quiz || quiz.dataset.quizInit === 'true') return;
    quiz.dataset.quizInit = 'true';

    // The result's mood word ("I'm a [Serenity]") is rendered by Liquid into each
    // card's data-mood (derived from the block's affinity). The JS just reads it.
    const form = quiz.querySelector('[data-quiz-form]');
    const steps = [...quiz.querySelectorAll('[data-quiz-step]')];
    const total = steps.length;
    const backBtn = quiz.querySelector('[data-quiz-back]');
    const nextBtn = quiz.querySelector('[data-quiz-next]');
    const finishBtn = quiz.querySelector('[data-quiz-finish]');
    const progress = quiz.querySelector('[data-quiz-progress]');
    const stepCount = quiz.querySelector('[data-step-count]');
    const gateEl = quiz.querySelector('[data-quiz-gate]');
    const gateForm = quiz.querySelector('[data-quiz-gate-form]');
    const resultEl = quiz.querySelector('[data-quiz-result]');
    const resultSlot = quiz.querySelector('[data-result-slot]');
    const resultMood = quiz.querySelector('[data-result-mood]');
    const pool = quiz.querySelector('[data-scent-pool]');
    const gated = quiz.dataset.gate === 'true';
    if (!form || !steps.length || !pool) return;

    let current = 1;

    /* ---------- step navigation ---------- */
    function showStep(n) {
      steps.forEach((s) => {
        const isTarget = parseInt(s.dataset.quizStep, 10) === n;
        if (isTarget) {
          s.dataset.active = 'true';
          s.removeAttribute('data-leaving');
        } else {
          s.dataset.active = 'false';
        }
      });
      current = n;
      progress.style.setProperty('--p', `${(n / total) * 100}%`);
      stepCount.textContent = `Step ${n} of ${total}`;
      backBtn.hidden = n === 1;
      const last = n === total;
      nextBtn.hidden = last;
      // On the last step the result auto-reveals when an answer is picked, so the
      // manual button is unnecessary. Keep it only as a fallback for when the user
      // lands on the last step with an answer already chosen (e.g. after Back).
      finishBtn.hidden = !last || !answeredFor(n);
      syncButtons();
      // Focus the legend for screen readers. preventScroll keeps the page from
      // jumping to the quiz — without it, the init showStep(1) call scrolls the
      // (last-on-page) quiz into view on every load.
      const legend = steps[n - 1].querySelector('.quiz__q');
      if (legend) legend.setAttribute('tabindex', '-1'), legend.focus({ preventScroll: true });
    }

    function answeredFor(n) {
      return quiz.querySelector(`input[name="step-${n}"]:checked`);
    }

    function syncButtons() {
      const answered = !!answeredFor(current);
      nextBtn.setAttribute('aria-disabled', answered ? 'false' : 'true');
      finishBtn.setAttribute('aria-disabled', answered ? 'false' : 'true');
    }

    let advanceTimer = null;
    form.addEventListener('change', (e) => {
      if (!e.target.matches('[data-quiz-input]')) return;
      syncButtons();
      // Auto-advance for snappy UX — and on the last step, auto-reveal the result
      // (no manual button needed). Debounced so arrow-key selection on a radio
      // group settles on the final choice before firing.
      clearTimeout(advanceTimer);
      advanceTimer = setTimeout(() => {
        if (current < total) goNext();
        else finish();
      }, 280);
    });

    function goNext() {
      if (!answeredFor(current)) return;
      if (current < total) showStep(current + 1);
    }
    function goBack() {
      if (current > 1) showStep(current - 1);
    }
    nextBtn.addEventListener('click', goNext);
    backBtn.addEventListener('click', goBack);
    finishBtn.addEventListener('click', finish);

    /* ---------- scoring ---------- */
    function computeWinner() {
      const tally = {};
      quiz.querySelectorAll('[data-quiz-input]:checked').forEach((input) => {
        const a = input.dataset.affinity;
        tally[a] = (tally[a] || 0) + 1;
      });
      let winner = null;
      let max = -1;
      // Stable order: respect the order scents appear in the pool for ties.
      const order = [...pool.querySelectorAll('[data-scent-result]')].map((c) => c.dataset.affinity);
      const keys = Object.keys(tally).sort((a, b) => order.indexOf(a) - order.indexOf(b));
      keys.forEach((k) => {
        if (tally[k] > max) {
          max = tally[k];
          winner = k;
        }
      });
      return winner;
    }

    /* ---------- finish / gate / reveal ---------- */
    function finish() {
      if (!answeredFor(current)) return;
      form.hidden = true;
      quiz.querySelector('.quiz__head').hidden = true;
      if (gated && gateEl) {
        gateEl.hidden = false;
        const input = gateEl.querySelector('input');
        if (input) input.focus();
      } else {
        reveal();
      }
    }

    if (gateForm) {
      gateForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const email = gateForm.querySelector('input[type="email"]').value.trim();
        if (!email) return;
        submitKlaviyo(email);
        try {
          localStorage.setItem('pp:quiz-email', email);
        } catch (err) {}
        gateEl.hidden = true;
        reveal();
      });
    }

    function reveal() {
      const winner = computeWinner() || (pool.querySelector('[data-scent-result]') || {}).dataset?.affinity;
      let card = pool.querySelector(`[data-scent-result][data-affinity="${winner}"]`);
      if (!card) card = pool.querySelector('[data-scent-result]');
      if (!card) return;

      resultMood.textContent = card.dataset.mood;

      // Move the matched card into the result slot and wash the section colour.
      resultSlot.innerHTML = '';
      const clone = card.cloneNode(true);
      resultSlot.appendChild(clone);
      resultEl.hidden = false;

      if (Pamper.applyTheme) {
        Pamper.applyTheme(
          {
            '--pp-primary': card.dataset.primary,
            '--pp-secondary': card.dataset.secondary,
            '--pp-bg': card.dataset.bg,
          },
          quiz
        );
      }

      // Render the radar in the revealed card.
      const radar = clone.querySelector('[data-scent-radar]');
      if (radar && Pamper.renderRadar) {
        radar.dataset.rendered = 'false';
        Pamper.renderRadar(radar);
      }
      if (Pamper.observeReveals) Pamper.observeReveals(resultEl);
      // Let hosts (e.g. the pop-up) know the shopper reached a match, so they can
      // remember it and not nag again. Fire before the scroll nicety so a missing
      // scrollIntoView never swallows it.
      quiz.dispatchEvent(new CustomEvent('pp:quiz-reveal', { bubbles: true, detail: { affinity: winner } }));
      if (resultEl.scrollIntoView) resultEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* ---------- retake ---------- */
    quiz.querySelector('[data-quiz-retake]').addEventListener('click', () => {
      quiz.querySelectorAll('[data-quiz-input]:checked').forEach((i) => (i.checked = false));
      resultEl.hidden = true;
      form.hidden = false;
      quiz.querySelector('.quiz__head').hidden = false;
      showStep(1);
      quiz.querySelector('.quiz__head').scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    /* ---------- Klaviyo ---------- */
    function submitKlaviyo(email) {
      const companyId = Pamper.settings && Pamper.settings.klaviyoCompanyId;
      const listId = Pamper.settings && Pamper.settings.klaviyoListId;
      if (!companyId) return; // Not configured — email kept locally only.
      // Klaviyo client-side subscription endpoint.
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            custom_source: 'Scent Quiz',
          },
          relationships: listId
            ? { list: { data: { type: 'list', id: listId } } }
            : undefined,
        },
      };
      fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload),
      }).catch(() => {});
    }

    /* ---------- init ---------- */
    showStep(1);
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-quiz]').forEach(initQuiz);
  }

  // Exposed so the pop-up controller can (re)initialise a quiz it reveals.
  Pamper.initQuiz = initQuiz;
  Pamper.initQuizzes = initAll;

  initAll();
  // Re-init sections re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
